import * as Y from "yjs";
import { cardFields, getBoardTypes, LOCAL, noteFragment } from "./doc.js";
import { fragmentText, fragmentToJSON, jsonToFragment } from "./richtext.js";

export interface SerializedCard {
  fields: Record<string, unknown>;
  body?: unknown;
  arrays?: Record<string, unknown[]>;
  maps?: Record<string, Record<string, unknown>>;
}

/** Plain-JSON snapshot of a card including nested Y types. */
export function serializeCard(ymap: Y.Map<unknown>): SerializedCard {
  const out: SerializedCard = { fields: {} };
  // Synced notes and clones carry a copy of the shared body so they survive moves and restores.
  const shared = noteFragment(ymap);
  if (shared && typeof ymap.get("syncedKey") === "string") out.body = fragmentToJSON(shared);
  ymap.forEach((v, k) => {
    if (v instanceof Y.XmlFragment) out.body ??= fragmentToJSON(v);
    else if (v instanceof Y.Array) (out.arrays ??= {})[k] = v.toJSON() as unknown[];
    else if (v instanceof Y.Map) (out.maps ??= {})[k] = v.toJSON() as Record<string, unknown>;
    else if (v instanceof Y.Text) out.fields[k] = v.toString();
    else out.fields[k] = v;
  });
  return out;
}

export interface DeserializeOpts {
  id?: string;
  userId?: string;
  x?: number;
  y?: number;
  parent?: string | null;
  unsorted?: boolean;
  origin?: unknown;
}

/** Re-create a card from its snapshot inside a doc. */
export function deserializeCard(doc: Y.Doc, snap: SerializedCard, opts: DeserializeOpts = {}): string {
  const { cards } = getBoardTypes(doc);
  const id = opts.id ?? (snap.fields.id as string) ?? crypto.randomUUID();
  doc.transact(() => {
    const card = new Y.Map<unknown>();
    cards.set(id, card);
    for (const [k, v] of Object.entries(snap.fields)) card.set(k, v);
    card.set("id", id);
    if (opts.x !== undefined) card.set("x", opts.x);
    if (opts.y !== undefined) card.set("y", opts.y);
    if (opts.parent !== undefined) {
      card.set("parent", opts.parent);
      if (!opts.parent) card.set("order", null);
    }
    if (opts.unsorted !== undefined) card.set("unsorted", opts.unsorted);
    if (opts.userId) {
      card.set("updatedBy", opts.userId);
      card.set("updatedAt", Date.now());
    }
    for (const [k, arr] of Object.entries(snap.arrays ?? {})) {
      const ya = new Y.Array<unknown>();
      card.set(k, ya);
      ya.insert(
        0,
        arr.map((item) => {
          if (item && typeof item === "object" && !Array.isArray(item)) {
            const m = new Y.Map<unknown>();
            for (const [ik, iv] of Object.entries(item as Record<string, unknown>)) m.set(ik, iv);
            return m;
          }
          return item;
        }),
      );
    }
    for (const [k, obj] of Object.entries(snap.maps ?? {})) {
      const ym = new Y.Map<unknown>();
      card.set(k, ym);
      for (const [mk, mv] of Object.entries(obj)) ym.set(mk, mv);
    }
    const key = snap.fields.syncedKey;
    if (typeof key === "string") {
      // Keep following the shared body when it exists here; otherwise seed a new shared body from the copy.
      const { synced } = getBoardTypes(doc);
      if (!synced.has(key)) {
        const frag = new Y.XmlFragment();
        synced.set(key, frag);
        if (snap.body) jsonToFragment(snap.body, frag);
      }
    } else if (snap.body !== undefined || ["note", "document"].includes(snap.fields.type as string)) {
      const frag = new Y.XmlFragment();
      card.set("body", frag);
      if (snap.body) jsonToFragment(snap.body, frag);
    }
  }, opts.origin ?? LOCAL);
  return id;
}

export function cardPreview(ymap: Y.Map<unknown>): string {
  const f = cardFields(ymap);
  const body = noteFragment(ymap);
  const text = body ? fragmentText(body) : "";
  return (f.title || f.text || text || f.name || f.url || f.hex || f.type).toString().slice(0, 120);
}
